import { DragEvent, useCallback, useState } from "react";
import { useAppStore } from "../../../../../hooks/useStores";

type UseFiboFileUploadProps = {
	activeTab: "generate" | "refine";
};

export const useFiboFileUpload = ({ activeTab }: UseFiboFileUploadProps) => {
	const { textToImageStore } = useAppStore();
	const [isDragging, setIsDragging] = useState(false);

	const uploadImageFile = useCallback(
		async (file: File) => {
			const syntheticEvent = {
				target: {
					files: [file],
					value: "",
				},
			} as unknown as React.ChangeEvent<HTMLInputElement>;

			await textToImageStore.handleUploadImage(syntheticEvent);
		},
		[textToImageStore],
	);

	const handleDragEnter = useCallback(
		(e: DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			if (activeTab !== "refine" && e.dataTransfer.types.includes("Files")) {
				setIsDragging(true);
			}
		},
		[activeTab],
	);

	const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();

		const relatedTarget = e.relatedTarget as Node;
		if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
			setIsDragging(false);
		}
	}, []);

	const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDrop = useCallback(
		async (e: DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);

			if (activeTab === "refine") return;

			const files = Array.from(e.dataTransfer.files);
			const imageFile = files.find((file) =>
				["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type),
			);

			if (imageFile) {
				await uploadImageFile(imageFile);
			}
		},
		[activeTab, uploadImageFile],
	);

	const handlePaste = useCallback(
		async (e: React.ClipboardEvent<HTMLDivElement>) => {
			if (activeTab === "refine") return;

			const items = Array.from(e.clipboardData.items);
			const imageItem = items.find((item) => item.type.startsWith("image/"));

			if (imageItem) {
				e.preventDefault();

				const file = imageItem.getAsFile();
				if (file) {
					await uploadImageFile(file);
				}
			}
		},
		[activeTab, uploadImageFile],
	);

	return {
		isDragging,
		handleDragEnter,
		handleDragLeave,
		handleDragOver,
		handleDrop,
		handlePaste,
	};
};
